/**
 * Safe Storage Wrapper for WebView2 Compatibility
 *
 * WebView2 can freeze when synchronous localStorage operations are performed
 * during module initialization. This wrapper:
 * 1. Wraps all operations in try/catch
 * 2. Provides a no-op storage option for when localStorage is problematic
 * 3. Defers read operations to prevent blocking
 */

// CRITICAL: Persistence is ALWAYS disabled initially
// It can be enabled later after the app has rendered
let persistenceEnabled = false;

// Cache for deferred reads
const storageCache: Map<string, string | null> = new Map();
let cacheInitialized = false;

/**
 * Initialize the storage cache asynchronously
 * This should be called early in app initialization
 */
export async function initializeStorageCache(): Promise<void> {
  if (cacheInitialized) return;

  return new Promise((resolve) => {
    // Use setTimeout to ensure we don't block the main thread
    setTimeout(() => {
      try {
        if (typeof localStorage !== 'undefined') {
          // Read all localStorage keys into cache
          const keys = Object.keys(localStorage);
          for (const key of keys) {
            try {
              storageCache.set(key, localStorage.getItem(key));
            } catch (e) {
              console.warn(`[SafeStorage] Failed to cache key: ${key}`, e);
            }
          }
        }
      } catch (e) {
        console.warn('[SafeStorage] Failed to initialize cache', e);
      }
      cacheInitialized = true;
      resolve();
    }, 0);
  });
}

/**
 * Disable persistence entirely (useful for debugging WebView2 issues)
 */
export function disablePersistence(): void {
  persistenceEnabled = false;
}

/**
 * Enable persistence
 */
export function enablePersistence(): void {
  persistenceEnabled = true;
}

/**
 * Check if persistence is enabled
 */
export function isPersistenceEnabled(): boolean {
  return persistenceEnabled;
}

/**
 * Safe localStorage wrapper that won't throw or block
 */
export const safeLocalStorage = {
  getItem: (name: string): string | null => {
    if (!persistenceEnabled) {
      return null;
    }

    // Return from cache if available
    if (storageCache.has(name)) {
      return storageCache.get(name) ?? null;
    }

    try {
      const value = localStorage.getItem(name);
      storageCache.set(name, value);
      return value;
    } catch (error) {
      console.warn(`[SafeStorage] Failed to read: ${name}`, error);
      return null;
    }
  },

  setItem: (name: string, value: string): void => {
    if (!persistenceEnabled) {
      return;
    }

    // Update cache immediately
    storageCache.set(name, value);

    // Write to localStorage asynchronously to avoid blocking
    setTimeout(() => {
      try {
        localStorage.setItem(name, value);
      } catch (error) {
        console.warn(`[SafeStorage] Failed to write: ${name}`, error);
      }
    }, 0);
  },

  removeItem: (name: string): void => {
    if (!persistenceEnabled) {
      return;
    }

    // Update cache immediately
    storageCache.delete(name);

    // Remove from localStorage asynchronously
    setTimeout(() => {
      try {
        localStorage.removeItem(name);
      } catch (error) {
        console.warn(`[SafeStorage] Failed to remove: ${name}`, error);
      }
    }, 0);
  },
};

/**
 * Return one stable adapter so stores created before bootstrap keep using the
 * persistence gate after enablePersistence is called.
 */
export function getStorage() {
  return safeLocalStorage;
}
